import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import StripeTrustBadge from '../components/common/StripeTrustBadge';
import {
  Card,
  Typography,
  Button,
  Space,
  Tag,
  Divider,
  Row,
  Col,
  Spin,
  Alert,
  Statistic,
  List,
  Progress,
  Tooltip,
  Grid,
} from 'antd';
import {
  CreditCardOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  UndoOutlined,
  UserOutlined,
  FileTextOutlined,
  RocketOutlined,
  ShoppingCartOutlined,
  ThunderboltOutlined,
  TeamOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { App } from 'antd';
import {
  useGetSubscriptionStatusQuery,
  useGetPlansQuery,
  useCancelSubscriptionMutation,
  useReactivateSubscriptionMutation,
  useCreatePortalSessionMutation,
  useChangePlanMutation,
  useGetExtraUsersOfferQuery,
  useGetStampBalanceQuery,
  useGetStampPackagesQuery,
  StampPackage,
} from '../services/stripeApi';
import { useGetUserLimitInfoQuery } from '../services/usersApi';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { useRestaurantTimeZone } from '../hooks/useTimeZone';
import { updateSubscriptionInfo } from '../store/slices/authSlice';
import EmbeddedCheckoutModal from '../components/stripe/EmbeddedCheckoutModal';
import BuyExtraUsersModal from '../components/stripe/BuyExtraUsersModal';
import { BUY_EXTRA_USERS_PARAM, BUY_EXTRA_USERS_VALUE, SUPPORT_WHATSAPP_URL } from '../constants/moduleKeys';
import { formatPlanPrice } from '../utils/currencyHelpers';

const { Title, Text } = Typography;

const SubscriptionManagement: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { message, modal } = App.useApp();
  useAppSelector((state) => state.auth);
  const { formatInTimeZone } = useRestaurantTimeZone();

  const { data: subscriptionStatus, isLoading: loadingStatus, refetch } = useGetSubscriptionStatusQuery();
  const { data: plans, isLoading: loadingPlans } = useGetPlansQuery();
  const { data: limitInfo, refetch: refetchLimitInfo } = useGetUserLimitInfoQuery();
  const [cancelSubscription, { isLoading: canceling }] = useCancelSubscriptionMutation();
  const [reactivateSubscription, { isLoading: reactivating }] = useReactivateSubscriptionMutation();
  const [createPortalSession, { isLoading: creatingPortal }] = useCreatePortalSessionMutation();
  const [changePlan, { isLoading: changingPlan }] = useChangePlanMutation();
  const screens = Grid.useBreakpoint();
  const [searchParams, setSearchParams] = useSearchParams();

  // Usuarios adicionales: la oferta solo tiene sentido con una suscripción viva.
  const {
    data: extraUsersOffer,
    isLoading: loadingExtraUsersOffer,
    isFetching: fetchingExtraUsersOffer,
  } = useGetExtraUsersOfferQuery(undefined, { skip: !subscriptionStatus?.hasSubscription });
  const [buyUsersOpen, setBuyUsersOpen] = useState(false);

  // Stamp balance hooks
  const { data: stampBalance, isLoading: loadingStamps, refetch: refetchStamps } = useGetStampBalanceQuery();
  const { data: stampPackages } = useGetStampPackagesQuery();
  const [selectedPackage, setSelectedPackage] = useState<StampPackage | null>(null);
  const [showStampCheckoutModal, setShowStampCheckoutModal] = useState(false);

  const isLoading = loadingStatus || loadingPlans;

  // Update Redux state when subscription status changes
  useEffect(() => {
    if (subscriptionStatus && limitInfo) {
      const isActive = subscriptionStatus.status === 'active' || subscriptionStatus.status === 'trialing';

      dispatch(updateSubscriptionInfo({
        plan: subscriptionStatus.planName || limitInfo.plan,
        isTrialExpired: !isActive && limitInfo.isTrialExpired,
        maxUsers: limitInfo.maxUsers,
        trialEndDate: subscriptionStatus.trialEnd || limitInfo.trialEndDate,
      }));
    }
  }, [subscriptionStatus, limitInfo, dispatch]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'processing';
      case 'past_due':
        return 'warning';
      case 'canceled':
      case 'unpaid':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Activa';
      case 'trialing':
        return 'Periodo de Prueba';
      case 'past_due':
        return 'Pago Pendiente';
      case 'canceled':
        return 'Cancelada';
      case 'unpaid':
        return 'Sin Pagar';
      case 'none':
        return 'Sin Suscripción';
      default:
        return status;
    }
  };

  const handleCancelSubscription = () => {
    modal.confirm({
      title: '¿Cancelar Suscripción?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Tu suscripción se cancelará al final del periodo actual.</p>
          <p>Podrás seguir usando el servicio hasta: <strong>{subscriptionStatus?.currentPeriodEnd ? formatInTimeZone(subscriptionStatus.currentPeriodEnd, 'DD/MM/YYYY') : 'N/A'}</strong></p>
        </div>
      ),
      okText: 'Sí, Cancelar',
      okType: 'danger',
      cancelText: 'No, Mantener',
      onOk: async () => {
        try {
          await cancelSubscription().unwrap();
          message.success('Tu suscripción ha sido programada para cancelarse');
          refetch();
        } catch (error) {
          message.error('Error al cancelar la suscripción');
        }
      },
    });
  };

  const handleReactivateSubscription = async () => {
    try {
      await reactivateSubscription().unwrap();
      message.success('Tu suscripción ha sido reactivada exitosamente');
      refetch();
    } catch (error) {
      message.error('Error al reactivar la suscripción');
    }
  };

  const handleManagePayment = async () => {
    try {
      const result = await createPortalSession().unwrap();
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error: any) {
      message.error(error?.data?.message || 'Error al abrir el portal de pagos');
    }
  };

  // Cambiar de plan modifica la suscripción que ya existe (con prorrateo). Antes abría
  // un checkout nuevo: Stripe creaba una segunda suscripción y cobraba los dos planes.
  const handleUpgradePlan = (planName: string, displayName: string, price: number) => {
    modal.confirm({
      title: `¿Cambiar al plan ${displayName}?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>El cambio es inmediato y se aplica a tu suscripción actual.</p>
          <p>
            Desde tu próxima factura pagarás ${price} MXN al mes
            {planQuantity > 1 ? ` por sucursal (${planQuantity} sucursales)` : ''}, más la diferencia
            proporcional de los días que quedan de este periodo.
          </p>
        </div>
      ),
      okText: 'Cambiar plan',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await changePlan({ planName }).unwrap();
          message.success(`Tu plan ahora es ${displayName}`);
          refetch();
          refetchLimitInfo();
        } catch (error: any) {
          message.error(error?.data?.message || 'No se pudo cambiar el plan');
        }
      },
    });
  };

  // Handle stamp package purchase
  const handleBuyStampPackage = (pkg: StampPackage) => {
    setSelectedPackage(pkg);
    setShowStampCheckoutModal(true);
  };

  // Handle stamp checkout completion
  const handleStampCheckoutComplete = () => {
    message.success('¡Paquete de timbres comprado exitosamente!');
    setShowStampCheckoutModal(false);
    setSelectedPackage(null);
    refetchStamps();
  };

  // Handle stamp checkout close
  const handleStampCheckoutClose = () => {
    setShowStampCheckoutModal(false);
    setSelectedPackage(null);
  };

  // Calculate stamp usage percentage
  const getStampUsagePercent = () => {
    if (!stampBalance || stampBalance.timbresPlanMensual === 0) return 0;
    return Math.round((stampBalance.timbresPlanConsumidos / stampBalance.timbresPlanMensual) * 100);
  };

  const getStampProgressStatus = () => {
    const percent = getStampUsagePercent();
    if (percent >= 90) return 'exception';
    if (percent >= 70) return 'active';
    return 'normal';
  };

  const getCurrentPlanInfo = () => {
    if (!plans || !subscriptionStatus) return null;
    return plans.find(p => p.name.toLowerCase() === subscriptionStatus.planName?.toLowerCase());
  };

  const currentPlanInfo = getCurrentPlanInfo();

  // Los extras (usuarios adicionales, etc.) se contratan encima del plan y se muestran
  // aparte. planName siempre trae el plan base: el backend ya no deja que un extra lo pise.
  const addons = subscriptionStatus?.addons ?? [];
  const extraUsers = subscriptionStatus?.extraUsers ?? 0;
  const addonsMonthlyTotal = subscriptionStatus?.addonsMonthlyTotal ?? 0;
  // Multi-restaurante: cada sucursal es +1 de cantidad del plan, así que el cargo
  // real es precio × cantidad. Sin esto, con dos sucursales la pantalla pintaba
  // la mitad de lo que cobra la tarjeta.
  const planQuantity = Math.max(1, subscriptionStatus?.planQuantity ?? 1);

  const isTrialing = subscriptionStatus?.status === 'trialing';

  // Asientos que tiene sin pagarlos: los dio soporte, o bajó de plan y el límite no baja
  // solo. Sin nombrarlos, "5 del plan + 2 adicionales" no cuadra con un límite de 9.
  const unpaidSeats = currentPlanInfo && limitInfo
    ? Math.max(0, limitInfo.maxUsers - (currentPlanInfo.maxUsers + extraUsers))
    : 0;

  // Desde Usuarios ("Comprar usuarios") se llega con ?comprar=usuarios: se abre la compra
  // si se puede y, si no, al menos se muestra la sección con el motivo.
  useEffect(() => {
    if (searchParams.get(BUY_EXTRA_USERS_PARAM) !== BUY_EXTRA_USERS_VALUE) return;
    if (!extraUsersOffer || fetchingExtraUsersOffer) return;

    if (extraUsersOffer.available) {
      setBuyUsersOpen(true);
    }
    document.getElementById('usuarios-adicionales')?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const next = new URLSearchParams(searchParams);
    next.delete(BUY_EXTRA_USERS_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, extraUsersOffer, fetchingExtraUsersOffer]);

  // Si la compra deja de estar disponible con la ventana abierta (se programó la
  // cancelación, falló Stripe), se cierra de verdad: si no, volvía a abrirse sola en cuanto
  // la oferta regresaba.
  useEffect(() => {
    if (extraUsersOffer && !extraUsersOffer.available) {
      setBuyUsersOpen(false);
    }
  }, [extraUsersOffer]);

  // El conteo de sucursales puede no venir si el Facturador desplegado es anterior al
  // campo; sin esta guarda la pantalla diría "compartidos entre tus 0 sucursales".
  const sucursalesCompartidas = (stampBalance?.cuentasVinculadas ?? 0) >= 2
    ? `tus ${stampBalance!.cuentasVinculadas} sucursales`
    : 'varias sucursales';

  // Extras totales: los del pool antiguo también se consumen y cuentan para el total, así
  // que el desglose tiene que incluirlos o no cuadra con "Puedes timbrar".
  const extrasDisponibles = stampBalance
    ? stampBalance.timbresExtraTotalDisponibles
      ?? (stampBalance.timbresExtraDisponibles + (stampBalance.timbresExtraPoolDisponibles ?? 0))
    : 0;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
      {/* Embedded Checkout Modal for Stamp Package Purchase */}
      <EmbeddedCheckoutModal
        open={showStampCheckoutModal}
        checkoutType="stamp_package"
        packageName={selectedPackage?.nombre || ''}
        planName=""
        planDisplayName={`${selectedPackage?.cantidadTimbres || 0} Timbres`}
        onClose={handleStampCheckoutClose}
        onComplete={handleStampCheckoutComplete}
      />

      {/* Montado mientras haya oferta, aunque esté cerrado o ya no se pueda comprar: si una
          compra no se confirmó, el reintento tiene que salir de la misma instancia (mismo id). */}
      {extraUsersOffer && (
        <BuyExtraUsersModal
          open={buyUsersOpen && extraUsersOffer.available}
          offer={extraUsersOffer}
          currentLimit={limitInfo?.maxUsers}
          currentMonthly={currentPlanInfo ? currentPlanInfo.price * planQuantity + addonsMonthlyTotal : undefined}
          nextChargeDate={isTrialing ? subscriptionStatus?.trialEnd : subscriptionStatus?.currentPeriodEnd}
          isTrialing={isTrialing}
          onClose={() => setBuyUsersOpen(false)}
          onPurchased={() => setBuyUsersOpen(false)}
        />
      )}

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Title level={2} style={{ margin: 0 }}>Administrar Suscripción</Title>
        </div>

        {/* Current Subscription Status */}
        <Card>
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <Space direction="vertical" size="small">
                <Text type="secondary">Estado de la Suscripción</Text>
                <Space>
                  <Tag
                    color={getStatusColor(subscriptionStatus?.status || 'none')}
                    icon={subscriptionStatus?.status === 'active' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                    style={{ fontSize: '14px', padding: '4px 12px' }}
                  >
                    {getStatusText(subscriptionStatus?.status || 'none')}
                  </Tag>
                  {subscriptionStatus?.cancelAtPeriodEnd && (
                    <Tag color="warning">Se cancela al final del periodo</Tag>
                  )}
                </Space>
              </Space>
            </Col>
            <Col xs={24} md={12}>
              <Space direction="vertical" size="small">
                <Text type="secondary">
                  {subscriptionStatus?.status === 'trialing' ? 'Plan Contratado' : 'Plan Actual'}
                </Text>
                <Title level={4} style={{ margin: 0, color: 'var(--color-primary)' }}>
                  {currentPlanInfo?.displayName || subscriptionStatus?.planName || 'Sin Plan'}
                </Title>
                {addons.length > 0 && (
                  <Space size={[4, 4]} wrap>
                    <Text type="secondary" style={{ fontSize: 12 }}>Extras contratados:</Text>
                    {addons.map((addon, index) => (
                      <Tag key={`${addon.name}-${index}`} color="cyan" style={{ margin: 0 }}>
                        {addon.quantity > 1 ? `${addon.quantity} × ` : ''}{addon.name}
                        {addon.amount != null ? ` · $${addon.amount}` : ''}
                      </Tag>
                    ))}
                  </Space>
                )}
              </Space>
            </Col>
            {subscriptionStatus?.trialEnd && subscriptionStatus.status === 'trialing' && (
              <Col xs={24}>
                <Alert
                  message="Periodo de Prueba Activo"
                  description={
                    <span>
                      Estás disfrutando de <strong>7 días gratis</strong> del plan <strong>{currentPlanInfo?.displayName || subscriptionStatus?.planName}</strong>.
                      Tu periodo de prueba finaliza el <strong>{formatInTimeZone(subscriptionStatus.trialEnd, 'DD [de] MMMM [de] YYYY')}</strong>.
                      Después de esa fecha, continuarás con el plan {currentPlanInfo?.displayName || subscriptionStatus?.planName} y se realizará el primer cobro de <strong>${(currentPlanInfo?.price || 0) + addonsMonthlyTotal} MXN</strong>.
                    </span>
                  }
                  type="info"
                  showIcon
                  icon={<ClockCircleOutlined />}
                />
              </Col>
            )}
            {subscriptionStatus?.currentPeriodEnd && subscriptionStatus.status === 'active' && !subscriptionStatus.cancelAtPeriodEnd && (
              <Col xs={24} md={12}>
                <Statistic
                  title="Próximo Cobro"
                  value={formatInTimeZone(subscriptionStatus.currentPeriodEnd, 'DD/MM/YYYY')}
                  prefix={<CreditCardOutlined />}
                />
              </Col>
            )}
            {/* Cancellation Warning Alert */}
            {subscriptionStatus?.cancelAtPeriodEnd && (
              <Col xs={24}>
                <Alert
                  message={
                    <Space>
                      <WarningOutlined />
                      <strong>Suscripción Programada para Cancelarse</strong>
                    </Space>
                  }
                  description={
                    <div>
                      <p style={{ marginBottom: 12 }}>
                        Tu suscripción se cancelará automáticamente el{' '}
                        <strong style={{ color: 'var(--color-error-text)' }}>
                          {/* cancelAt: cancelada desde el portal de Stripe, puede no ser el fin del periodo. */}
                          {subscriptionStatus.cancelAt || subscriptionStatus.currentPeriodEnd
                            ? formatInTimeZone(
                                (subscriptionStatus.cancelAt || subscriptionStatus.currentPeriodEnd)!,
                                'DD [de] MMMM [de] YYYY')
                            : 'N/A'}
                        </strong>.
                        Después de esta fecha, perderás acceso a las funcionalidades del plan{' '}
                        <strong>{currentPlanInfo?.displayName || subscriptionStatus?.planName}</strong>.
                      </p>
                      <Button
                        type="primary"
                        icon={<UndoOutlined />}
                        onClick={handleReactivateSubscription}
                        loading={reactivating}
                        style={{ background: 'var(--color-success)', borderColor: 'var(--color-success)' }}
                      >
                        Reactivar Suscripción
                      </Button>
                    </div>
                  }
                  type="error"
                  showIcon
                  icon={<WarningOutlined style={{ fontSize: 24 }} />}
                  style={{
                    border: '2px solid var(--color-error)',
                    background: 'var(--bg-error-light)',
                  }}
                />
              </Col>
            )}
          </Row>
        </Card>

        {/* Current Plan Details */}
        {currentPlanInfo && (
          <Card title="Detalles de tu Plan">
            <Row gutter={[24, 24]}>
              <Col xs={24} sm={8}>
                {/* El total, no solo el plan: quien tiene un usuario adicional paga 648,
                    y ver 599 no cuadra con el cargo de su tarjeta. Con sucursales, el
                    plan se multiplica por la cantidad contratada. */}
                <Statistic
                  title="Precio Mensual"
                  value={currentPlanInfo.price * planQuantity + addonsMonthlyTotal}
                  prefix="$"
                  suffix="MXN"
                />
                {(addonsMonthlyTotal > 0 || planQuantity > 1) && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {planQuantity > 1
                      ? `$${currentPlanInfo.price} × ${planQuantity} sucursales`
                      : `$${currentPlanInfo.price} del plan`}
                    {addonsMonthlyTotal > 0 ? ` + $${addonsMonthlyTotal} de extras` : ''}
                  </Text>
                )}
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title={planQuantity > 1 ? 'Usuarios por Sucursal' : 'Usuarios Incluidos'}
                  value={currentPlanInfo.maxUsers + extraUsers}
                  prefix={<UserOutlined />}
                  suffix={
                    extraUsers > 0
                      ? <Text type="secondary" style={{ fontSize: 12 }}>({currentPlanInfo.maxUsers} del plan + {extraUsers} extra{extraUsers > 1 ? 's' : ''})</Text>
                      : undefined
                  }
                />
              </Col>
              <Col xs={24} sm={8}>
                {/* La cuota mensual es plan × sucursales activas (mismo criterio que
                    el reset en Facturador). */}
                <Statistic
                  title="Timbres CFDI/mes"
                  value={currentPlanInfo.cfdiStamps * planQuantity}
                  prefix={<FileTextOutlined />}
                  suffix={
                    planQuantity > 1 && currentPlanInfo.cfdiStamps > 0
                      ? <Text type="secondary" style={{ fontSize: 12 }}>({currentPlanInfo.cfdiStamps} × {planQuantity} sucursales)</Text>
                      : undefined
                  }
                />
              </Col>
              <Col xs={24}>
                <Divider orientation="left">Características Incluidas</Divider>
                <List
                  size="small"
                  dataSource={currentPlanInfo.features}
                  renderItem={(item) => (
                    <List.Item>
                      <CheckCircleOutlined style={{ color: 'var(--color-success)', marginRight: 8 }} />
                      {item}
                    </List.Item>
                  )}
                />
              </Col>
            </Row>
          </Card>
        )}

        {/* Usuarios adicionales: antes solo soporte podía agregarlos, a mano en Stripe. */}
        {subscriptionStatus?.hasSubscription && (
          <Card
            id="usuarios-adicionales"
            title={
              <Space>
                <TeamOutlined />
                <span>Usuarios</span>
              </Space>
            }
          >
            <Row gutter={[24, 16]} align="middle">
              <Col xs={24} md={16}>
                <Space direction="vertical" size={4}>
                  {limitInfo && (
                    <div>
                      <Text strong style={{ fontSize: 20, fontVariantNumeric: 'tabular-nums' }}>
                        {limitInfo.currentUsers} de {limitInfo.maxUsers}
                      </Text>{' '}
                      <Text type="secondary">
                        usuarios en uso{planQuantity > 1 ? ' en esta sucursal' : ''}
                      </Text>
                    </div>
                  )}
                  {currentPlanInfo && (
                    <Text type="secondary">
                      {currentPlanInfo.maxUsers} incluido{currentPlanInfo.maxUsers === 1 ? '' : 's'} en tu plan
                      {extraUsers > 0 ? ` + ${extraUsers} adicional${extraUsers === 1 ? '' : 'es'}` : ''}
                      {unpaidSeats > 0 ? ` + ${unpaidSeats} sin costo` : ''}
                    </Text>
                  )}
                  {extraUsersOffer?.available && extraUsersOffer.unitAmount != null && (
                    <Text type="secondary">
                      Cada usuario adicional cuesta {formatPlanPrice(extraUsersOffer.unitAmount)}{' '}
                      {(extraUsersOffer.currency ?? 'mxn').toUpperCase()}{' '}
                      {extraUsersOffer.interval === 'year' ? 'al año' : 'al mes'}.
                    </Text>
                  )}
                </Space>
              </Col>
              <Col xs={24} md={8} style={{ display: 'flex', justifyContent: screens.md ? 'flex-end' : 'stretch' }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<UserAddOutlined />}
                  block={!screens.md}
                  loading={loadingExtraUsersOffer}
                  // Mientras la oferta se refresca, la cantidad actual puede venir vieja.
                  disabled={!extraUsersOffer?.available || fetchingExtraUsersOffer}
                  onClick={() => setBuyUsersOpen(true)}
                >
                  Comprar usuarios
                </Button>
              </Col>
              {extraUsersOffer && !extraUsersOffer.available && extraUsersOffer.unavailableReason && (
                <Col xs={24}>
                  <Alert
                    type="info"
                    showIcon
                    message={extraUsersOffer.unavailableReason}
                    action={
                      <Button size="small" href={SUPPORT_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                        Escríbenos
                      </Button>
                    }
                  />
                </Col>
              )}
              <Col xs={24}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  ¿Necesitas quitar usuarios adicionales o tienes dudas?{' '}
                  <a href={SUPPORT_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                    Escríbenos
                  </a>
                  .
                </Text>
              </Col>
            </Row>
          </Card>
        )}

        {/* Actions */}
        <Card title="Acciones">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {subscriptionStatus?.hasSubscription && (
              <Button
                type="primary"
                icon={<CreditCardOutlined />}
                onClick={handleManagePayment}
                loading={creatingPortal}
                block
                size="large"
              >
                Gestionar Método de Pago
              </Button>
            )}

            {subscriptionStatus?.hasSubscription && !subscriptionStatus.cancelAtPeriodEnd && (
              <Button
                danger
                onClick={handleCancelSubscription}
                loading={canceling}
                block
                size="large"
              >
                Cancelar Suscripción
              </Button>
            )}

            {!subscriptionStatus?.hasSubscription && (
              <Alert
                message="No tienes una suscripción activa"
                description="Selecciona un plan para comenzar a usar todas las funciones."
                type="warning"
                showIcon
                action={
                  <Button type="primary" onClick={() => navigate('/select-plan')}>
                    Ver Planes
                  </Button>
                }
              />
            )}
          </Space>
        </Card>

        {/* Upgrade Options */}
        {subscriptionStatus?.hasSubscription && plans && (
          <Card title="Cambiar de Plan">
            <Row gutter={[16, 16]}>
              {plans.map((planItem) => {
                const isCurrentPlan = planItem.name.toLowerCase() === subscriptionStatus.planName?.toLowerCase();
                const isUpgrade = planItem.price > (currentPlanInfo?.price || 0);

                return (
                  <Col xs={24} sm={8} key={planItem.name}>
                    <Card
                      size="small"
                      style={{
                        border: isCurrentPlan ? '2px solid var(--color-primary)' : undefined,
                        opacity: isCurrentPlan ? 0.7 : 1,
                      }}
                    >
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Title level={5} style={{ margin: 0 }}>{planItem.displayName}</Title>
                          {isCurrentPlan && <Tag color="green">Plan Actual</Tag>}
                        </div>
                        <Text strong style={{ fontSize: '20px', color: 'var(--color-primary)' }}>
                          ${planItem.price} MXN<Text type="secondary">/mes</Text>
                        </Text>
                        <Text type="secondary">
                          {planItem.maxUsers} usuarios | {planItem.cfdiStamps} timbres
                        </Text>
                        <Button
                          type={isUpgrade ? 'primary' : 'default'}
                          icon={isUpgrade ? <RocketOutlined /> : undefined}
                          disabled={isCurrentPlan || changingPlan}
                          loading={changingPlan}
                          onClick={() => handleUpgradePlan(planItem.name, planItem.displayName, planItem.price)}
                          block
                        >
                          {isCurrentPlan ? 'Plan Actual' : isUpgrade ? 'Mejorar' : 'Cambiar'}
                        </Button>
                      </Space>
                    </Card>
                  </Col>
                );
              })}
            </Row>
            <Alert
              style={{ marginTop: 16 }}
              message="Nota"
              description="Al cambiar de plan, se prorrateará el costo restante de tu plan actual. El cambio será efectivo inmediatamente."
              type="info"
              showIcon
            />
          </Card>
        )}

        {/* Stamp Balance Section - Show for active subscriptions and trialing */}
        {subscriptionStatus?.hasSubscription && (
        <Card
          title={
            <Space>
              <FileTextOutlined />
              <span>Timbres Fiscales (CFDI)</span>
            </Space>
          }
        >
          {/* Trial Period Notice */}
          {subscriptionStatus?.status === 'trialing' && (
            <Alert
              style={{ marginBottom: 24 }}
              message="Timbres disponibles al finalizar el periodo de prueba"
              description={
                <span>
                  Los <strong>{currentPlanInfo?.cfdiStamps || 0} timbres mensuales</strong> de tu plan{' '}
                  <strong>{currentPlanInfo?.displayName}</strong> estarán disponibles cuando termine tu periodo de prueba
                  el <strong>{subscriptionStatus.trialEnd ? formatInTimeZone(subscriptionStatus.trialEnd, 'DD [de] MMMM [de] YYYY') : 'N/A'}</strong>.
                  <br /><br />
                  Si necesitas facturar durante el periodo de prueba, puedes comprar un paquete de timbres a continuación.
                </span>
              }
              type="info"
              showIcon
              icon={<ClockCircleOutlined />}
            />
          )}

          {loadingStamps ? (
            <div style={{ textAlign: 'center', padding: '24px' }}>
              <Spin />
            </div>
          ) : stampBalance ? (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Only show plan/extra stamps cards if NOT in trial */}
              {subscriptionStatus?.status !== 'trialing' && (
                <>
                  <Row gutter={[24, 24]}>
                    {/* Plan Stamps */}
                    <Col xs={24} md={12}>
                      <Card
                        size="small"
                        style={{
                          background: 'linear-gradient(135deg, var(--bg-success-light) 0%, color-mix(in srgb, var(--color-success) 12%, var(--bg-primary)) 100%)',
                          border: '1px solid var(--border-success)'
                        }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text strong style={{ color: 'var(--color-success-text)' }}>Timbres del Plan</Text>
                            <Space size={4}>
                              {stampBalance.esCompartido && (
                                <Tooltip title={`${sucursalesCompartidas === 'varias sucursales' ? 'Varias de tus sucursales facturan' : `Tus ${sucursalesCompartidas} facturan`} con el mismo RFC, así que sus timbres mensuales se suman en una sola bolsa. Cualquiera de ellas puede usarlos.`}>
                                  <Tag color="cyan">Compartido</Tag>
                                </Tooltip>
                              )}
                              <Tooltip title="Se reinician mensualmente según tu plan">
                                <Tag color="green">Mensual</Tag>
                              </Tooltip>
                            </Space>
                          </div>
                          <div style={{ textAlign: 'center', padding: '16px 0' }}>
                            <Title level={2} style={{ margin: 0, color: 'var(--color-success-text)' }}>
                              {stampBalance.timbresPlanDisponibles}
                              <Text type="secondary" style={{ fontSize: '16px' }}> / {stampBalance.timbresPlanMensual}</Text>
                            </Title>
                            <Text type="secondary">disponibles</Text>
                          </div>
                          {stampBalance.timbresPlanMensual > 0 && (
                            <Progress
                              percent={getStampUsagePercent()}
                              status={getStampProgressStatus()}
                              showInfo={false}
                              strokeColor={getStampUsagePercent() >= 90 ? 'var(--color-error)' : getStampUsagePercent() >= 70 ? 'var(--color-warning)' : 'var(--color-success)'}
                            />
                          )}
                          {/* Con RFC compartido, la cifra de arriba es la bolsa común. Sin
                              esta línea, cada sucursal cree que ese total es solo suyo. */}
                          {stampBalance.esCompartido && (
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                              Compartidos entre {sucursalesCompartidas}
                              {!!stampBalance.timbresPlanAportadosCuenta && (
                                <> · esta aporta {stampBalance.timbresPlanAportadosCuenta} y lleva {stampBalance.timbresPlanConsumidosCuenta ?? 0}</>
                              )}
                            </Text>
                          )}
                          {stampBalance.fechaResetPlan && (
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              <ClockCircleOutlined style={{ marginRight: 4 }} />
                              Próximo reset: {formatInTimeZone(stampBalance.fechaResetPlan, 'DD/MM/YYYY')}
                            </Text>
                          )}
                        </Space>
                      </Card>
                    </Col>

                    {/* Extra Stamps */}
                    <Col xs={24} md={12}>
                      <Card
                        size="small"
                        style={{
                          background: 'linear-gradient(135deg, var(--bg-info-light) 0%, color-mix(in srgb, var(--color-info) 12%, var(--bg-primary)) 100%)',
                          border: '1px solid var(--border-info)'
                        }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text strong style={{ color: 'var(--color-info-text)' }}>Timbres Extra</Text>
                            <Tooltip title="Paquetes comprados que no expiran">
                              <Tag color="blue">Permanente</Tag>
                            </Tooltip>
                          </div>
                          <div style={{ textAlign: 'center', padding: '16px 0' }}>
                            <Title level={2} style={{ margin: 0, color: 'var(--color-info-text)' }}>
                              {extrasDisponibles}
                            </Title>
                            <Text type="secondary">disponibles</Text>
                          </div>
                          <Text type="secondary" style={{ fontSize: '12px', textAlign: 'center', display: 'block' }}>
                            <ThunderboltOutlined style={{ marginRight: 4 }} />
                            No expiran, se usan después del plan
                          </Text>
                        </Space>
                      </Card>
                    </Col>
                  </Row>

                  {/* Total and Alerts */}
                  <div style={{
                    background: 'var(--bg-secondary)',
                    padding: '16px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    {/* "Puedes timbrar" y no "Total": es exactamente lo que el sistema
                        dejará emitir desde esta sucursal (bolsa del plan + sus extras). */}
                    <Text type="secondary">Puedes timbrar: </Text>
                    <Text strong style={{ fontSize: '20px', color: stampBalance.totalDisponibles > 0 ? 'var(--color-primary)' : 'var(--color-error)' }}>
                      {stampBalance.totalDisponibles} timbres
                    </Text>
                    {stampBalance.esCompartido && (
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Incluye los timbres que no hayan usado tus otras sucursales
                        </Text>
                      </div>
                    )}
                    {!stampBalance.puedeTimbrar && (
                      <Alert
                        style={{ marginTop: 12 }}
                        message="Sin timbres disponibles"
                        description="Compra un paquete adicional para poder facturar."
                        type="error"
                        showIcon
                      />
                    )}
                    {stampBalance.alertaActiva && stampBalance.puedeTimbrar && (
                      <Alert
                        style={{ marginTop: 12 }}
                        message="Timbres bajos"
                        description={`Te quedan solo ${stampBalance.totalDisponibles} timbres. Considera comprar un paquete adicional.`}
                        type="warning"
                        showIcon
                      />
                    )}
                  </div>
                </>
              )}

              {/* For trialing users - show only extra stamps if they have any */}
              {subscriptionStatus?.status === 'trialing' && stampBalance.timbresExtraDisponibles > 0 && (
                <div style={{
                  background: 'var(--bg-secondary)',
                  padding: '16px',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <Text type="secondary">Timbres Extra Disponibles: </Text>
                  <Text strong style={{ fontSize: '20px', color: '#1890ff' }}>
                    {stampBalance.timbresExtraDisponibles} timbres
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Estos timbres fueron comprados y están disponibles para usar ahora.
                  </Text>
                </div>
              )}

              {/* Stamp Packages - Always show for both active and trialing users */}
              {stampPackages && stampPackages.length > 0 && (
                <>
                  <Divider orientation="left">
                    <Space>
                      <ShoppingCartOutlined />
                      <span>
                        {subscriptionStatus?.status === 'trialing'
                          ? 'Comprar Timbres para Facturar Durante el Trial'
                          : 'Comprar Paquetes de Timbres'}
                      </span>
                    </Space>
                  </Divider>
                  <Row gutter={[16, 16]}>
                    {stampPackages.map((pkg) => (
                      <Col xs={12} sm={6} key={pkg.id}>
                        <Card
                          size="small"
                          hoverable
                          style={{ textAlign: 'center' }}
                        >
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Tag color="purple" style={{ margin: 0 }}>{pkg.nombre}</Tag>
                            <Title level={3} style={{ margin: '8px 0', color: '#722ed1' }}>
                              {pkg.cantidadTimbres}
                            </Title>
                            <Text type="secondary">timbres</Text>
                            <Text strong style={{ color: 'var(--color-primary)', fontSize: '16px' }}>
                              ${pkg.precio} MXN
                            </Text>
                            <Button
                              type="primary"
                              icon={<ShoppingCartOutlined />}
                              onClick={() => handleBuyStampPackage(pkg)}
                              block
                              style={{ marginTop: 8 }}
                            >
                              Comprar
                            </Button>
                          </Space>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                  <Alert
                    style={{ marginTop: 8 }}
                    message={
                      subscriptionStatus?.status === 'trialing'
                        ? "¿Necesitas facturar durante el periodo de prueba? Compra un paquete de timbres. No expiran y se acumulan con los timbres de tu plan cuando termine el trial."
                        : "Los paquetes de timbres no expiran y se consumen después de agotar los timbres de tu plan mensual."
                    }
                    type="info"
                    showIcon
                  />
                </>
              )}
            </Space>
          ) : (
            <Alert
              message="No se pudo cargar la información de timbres"
              description="Intenta recargar la página. Si el problema persiste, contacta a soporte."
              type="warning"
              showIcon
            />
          )}
        </Card>
        )}

        {/* Stripe Security Notice */}
        <StripeTrustBadge showLogo />
      </Space>
    </div>
  );
};

export default SubscriptionManagement;
